import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { PageHeader, Panel, Field } from "../components/UI";
import "./ProfilesView.css";

const url = name => `/api/profiles/${encodeURIComponent(name)}`;

export function validateProfileName(name, names) {
  if (!name?.trim()) return "Enter a profile name.";
  if (!/^[a-z0-9][a-z0-9 _-]{0,99}$/i.test(name)) return "Use letters, numbers, spaces, underscores or hyphens (up to 100 characters).";
  if (names.some(existing => existing.toLowerCase() === name.toLowerCase())) return "A profile with that name already exists.";
  return null;
}

function scenarioName(config) {
  const id = config?.game?.scenarioId;
  return typeof id === "string" && id ? id.replace(/^\{[^}]*\}/, "").split(/[\\/]/).pop()
    .replace(/\.conf$/i, "").replace(/[_-]+/g, " ") : "Not available";
}

function Summary({ config }) {
  return <dl className="profile-summary">
    <div><dt>Scenario Name</dt><dd>{scenarioName(config)}</dd></div>
    <div><dt>Scenario ID</dt><dd>{config?.game?.scenarioId || "Not available"}</dd></div>
    <div><dt>Installed Mods</dt><dd>{Array.isArray(config?.game?.mods) ? config.game.mods.length : "Not available"}</dd></div>
    <div><dt>Last Modified</dt><dd>Not available</dd></div>
  </dl>;
}

export default function ProfilesView({ activeProfile, onProfileLoaded }) {
  const [names, setNames] = useState([]);
  const [selected, setSelected] = useState("");
  const [config, setConfig] = useState(null);
  const [known, setKnown] = useState({});
  const [importConfig, setImportConfig] = useState(null);
  const importFile = useRef(null);
  const operationLock = useRef(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);
  const [prompt, setPrompt] = useState(null);
  const [newName, setNewName] = useState("");
  const [nameError, setNameError] = useState(null);

  useEffect(() => {
    let current = true;
    Promise.all([axios.get("/api/profiles", { params: { details: "true" } }), axios.get("/api/config")]).then(([profiles, configuration]) => {
      if (!Array.isArray(profiles.data)) throw new Error("Invalid profiles");
      if (current) {
        const profileNames = profiles.data.map(profile => profile.name);
        setNames(profileNames);
        setKnown(Object.fromEntries(profiles.data.map(profile => [profile.name, profile.config])));
        setSelected(profileNames.includes(activeProfile) ? activeProfile : profileNames[0] || "");
        setConfig(configuration.data);
      }
    }).catch(() => { if (current) setFeedback({ error: true, message: "Profiles could not be loaded. Please refresh to try again." }); })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
    // Active profile changes are handled by successful operations below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async (operation, message) => {
    if (operationLock.current) return;
    operationLock.current = true;
    setBusy(true);
    setFeedback(null);
    try {
      await operation();
      const { data } = await axios.get("/api/profiles", { params: { details: "true" } });
      setNames(data.map(profile => profile.name));
      setKnown(Object.fromEntries(data.map(profile => [profile.name, profile.config])));
      setFeedback({ message });
      setPrompt(null);
    } catch (error) {
      setFeedback({ error: true, message: error.response?.data?.error || error.message || "Profile operation failed. Please try again." });
      try {
        const { data } = await axios.get("/api/profiles", { params: { details: "true" } });
        setNames(data.map(profile => profile.name));
        setKnown(Object.fromEntries(data.map(profile => [profile.name, profile.config])));
      } catch { /* Keep the last known list when refresh also fails. */ }
    } finally { operationLock.current = false; setBusy(false); }
  };

  const load = name => run(async () => {
    const { data } = await axios.get(url(name));
    setConfig(data);
    setKnown(previous => ({ ...previous, [name]: data }));
    setSelected(name);
    onProfileLoaded?.(name);
  }, `Loaded profile "${name}".`);

  const save = () => run(async () => {
    await axios.post(url(activeProfile));
    const { data } = await axios.get("/api/config");
    setConfig(data);
    setKnown(previous => ({ ...previous, [activeProfile]: data }));
  }, `Saved profile "${activeProfile}".`);

  const ask = (action, source = activeProfile) => {
    setPrompt({ action, source });
    setNewName("");
    setNameError(null);
  };
  const submitName = event => {
    event.preventDefault();
    const name = newName.trim();
    const error = validateProfileName(name, names);
    if (error) { setNameError(error); return; }
    const { action, source } = prompt;
    run(async () => {
      if (action === "Import") {
        await axios.post(`${url(name)}/import`, importConfig);
      } else if (action === "Save As") {
        const { data } = await axios.get("/api/config");
        await axios.post(`${url(name)}/import`, data);
        setConfig(data);
        setKnown(previous => ({ ...previous, [name]: data }));
        onProfileLoaded?.(name, false);
      } else {
        await axios.post(`${url(source)}/duplicate`, { destination: name });
        if (action === "Rename") {
          try { await axios.delete(url(source)); }
          catch (error) {
            // Preserve the new copy if source deletion fails or its response is lost.
            throw new Error(`Rename incomplete. The copy "${name}" was created; the original could not be confirmed deleted.`);
          }
          if (activeProfile === source) onProfileLoaded?.(name, false);
        }
        setKnown(previous => ({ ...previous, [name]: previous[source] }));
      }
      setSelected(name);
    }, `${action === "Import" ? "Imported" : action === "Rename" ? "Renamed" : action === "Duplicate" ? "Duplicated" : "Saved"} profile "${name}".`);
  };

  const exportProfile = name => run(async () => {
    const { data } = await axios.get(`${url(name)}/export`);
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const downloadUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = `${name}.json`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(downloadUrl), 0);
  }, `Exported profile "${name}".`);

  const readImport = event => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const data = JSON.parse(reader.result);
        if (!data?.game || typeof data.game.scenarioId !== "string" || !Array.isArray(data.game.mods)) throw new Error("Invalid configuration");
        setImportConfig(data);
        ask("Import");
        setNewName(file.name.replace(/\.json$/i, ""));
      } catch { setFeedback({ error: true, message: "Select a valid server configuration JSON file containing game.scenarioId and game.mods." }); }
    };
    reader.onerror = () => setFeedback({ error: true, message: "The JSON file could not be read." });
    reader.readAsText(file);
  };

  const remove = name => {
    if (name === "Default") return;
    if (!window.confirm(`Delete profile "${name}"?`)) return;
    run(async () => {
      await axios.delete(url(name));
      if (activeProfile === name) onProfileLoaded?.(null, false);
      if (selected === name) setSelected("");
    }, `Deleted profile "${name}". Current server configuration was preserved.`);
  };

  return <div className="page-stack profiles-page">
    <PageHeader title="Profiles" description="Manage complete server configurations." />
    {feedback && <p role={feedback.error ? "alert" : "status"} className={feedback.error ? "profile-error" : "save-feedback"}>{feedback.message}</p>}
    {loading && <p role="status">Loading profiles…</p>}
    <Panel title="Current Active Profile">
      <h3>{activeProfile && names.includes(activeProfile) ? activeProfile : "Current configuration (no tracked profile)"}</h3>
      <Summary config={config} />
      <div className="profile-toolbar">
        <Field label="Profile to load" htmlFor="profile-selection">
          <select id="profile-selection" value={selected} onChange={event => setSelected(event.target.value)} disabled={busy || loading}>
            <option value="">Select a profile</option>
            {names.map(name => <option key={name}>{name}</option>)}
          </select>
        </Field>
        <div className="button-row">
          <button disabled={busy || !selected} onClick={() => load(selected)}>Load Profile</button>
          <button disabled={busy || !activeProfile || !names.includes(activeProfile)} onClick={save}>Save Current</button>
          <button disabled={busy || !config} onClick={() => ask("Save As")}>Save As...</button>
          <button disabled={busy || !selected} onClick={() => ask("Duplicate", selected)}>Duplicate</button>
          <button disabled={busy || !selected || selected === "Default"} onClick={() => ask("Rename", selected)}>Rename</button>
          <button className="danger" disabled={busy || !selected || selected === "Default"} onClick={() => remove(selected)}>Delete</button>
          <button disabled={busy || !selected} onClick={() => exportProfile(selected)}>Export</button>
          <button disabled={busy || loading} onClick={() => importFile.current.click()}>Import</button>
          <input ref={importFile} hidden aria-label="Import JSON profile" type="file" accept=".json,application/json" disabled={busy || loading} onChange={readImport} />
        </div>
      </div>
    </Panel>
    {prompt && <Panel title={`${prompt.action} Profile`}>
      <form onSubmit={submitName} className="profile-name-form">
        <Field label="New profile name" htmlFor="new-profile-name">
          <input autoFocus id="new-profile-name" value={newName} disabled={busy} onChange={event => setNewName(event.target.value)} />
        </Field>
        {nameError && <p role="alert" className="profile-error">{nameError}</p>}
        <div className="button-row">
          <button disabled={busy} type="submit">Confirm {prompt.action}</button>
          <button disabled={busy} type="button" className="button-secondary" onClick={() => setPrompt(null)}>Cancel</button>
        </div>
      </form>
    </Panel>}
    {!loading && !names.length && <p className="muted">No profiles available.</p>}
    <section aria-label="Profile List" className="profile-cards">
      {names.map(name => <Panel key={name} title={name} className="profile-card">
        <Summary config={known[name]} />
        <div className="button-row">
          <button disabled={busy} onClick={() => load(name)}>Load</button>
          <button disabled={busy} onClick={() => ask("Duplicate", name)}>Duplicate</button>
          <button disabled={busy || name === "Default"} onClick={() => ask("Rename", name)}>Rename</button>
          <button disabled={busy || name === "Default"} className="danger" onClick={() => remove(name)}>Delete</button>
        </div>
      </Panel>)}
    </section>
    {busy && <p role="status">Updating profiles…</p>}
  </div>;
}
